import isUndefined from 'lodash.isundefined';
import PropTypes from 'prop-types';
import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

// components
import CartUpsellModalBase, { modalStyles, reasonsForExclusion } from '../CartUpsellModalBase';
import ModalBase from '@molecules/ModalBase';

// functionality
import { LoadingContext } from '@context/LoadingProvider';
import { addMembershipToCart, processCartErrors } from '@general/helpers/cart.helpers';
import productTypes from '@general/productTypes';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useMembersByCompany from '@hooks/apiLegacy/useMembersByCompany';
import useViewer from '@hooks/useViewer';
import useViewerCompanyIdEffective from '@hooks/useViewerCompanyIdEffective';

const CartUpsellMembershipModal = ({ isOpen, limitToMemberId, onClose }) => {
    // ------------------------------
    // State
    // ------------------------------

    const { setIsLoading } = useContext(LoadingContext);
    const [membersToExclude, setMembersToExclude] = useState();

    // ------------------------------
    // Hooks
    // ------------------------------

    const history = useHistory();
    const { viewer } = useViewer();
    const { viewerCompanyId } = useViewerCompanyIdEffective();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { coworkers } = useMembersByCompany(viewerCompanyId);

    // ------------------------------
    // Effects
    // ------------------------------

    useEffect(() => {
        if (!cart) {
            return;
        }

        const memberIdsInCart = cart.items
            .filter(item => item.product_type === productTypes.MEMBERSHIP)
            .map(item => ({
                id: item.member_id,
                reason: reasonsForExclusion.ALREADY_IN_CART,
            }));

        setMembersToExclude(memberIdsInCart);
    }, [cart]);

    // ------------------------------
    // Functions
    // ------------------------------

    const registerMembers = async ({ selectedMembers, shouldAddNewMember }) => {
        onClose();

        if (selectedMembers.length > 0) {
            setIsLoading(true);

            const cartResponse = await selectedMembers.reduce(async (previousPromise, member) => {
                await previousPromise;
                return addMembershipToCart({
                    cart,
                    memberId: member.id,
                    viewerId: viewer.recordId,
                    years: 3,
                });
            }, Promise.resolve());

            await processCartErrors({ cart: cartResponse, viewer });
            setIsLoading(false);
        }

        if (shouldAddNewMember) {
            history.push(`/membership/info/new`);
        }
    };

    // ------------------------------
    // Render
    // ------------------------------

    if (!coworkers) {
        return null;
    }

    return (
        <ModalBase
            isOpen={isOpen}
            shouldCloseOnOverlayClick={false}
            stylesBody={modalStyles.body}
            stylesContent={modalStyles.content}
            stylesOverlay={modalStyles.overlay}
            onClose={onClose}
        >
            <CartUpsellModalBase
                cart={cart}
                coworkers={coworkers}
                heading="Who do you want to register for ICSC Membership?"
                limitToMemberId={limitToMemberId}
                membersToExclude={membersToExclude}
                viewerId={viewer.recordId}
                onAddToCart={registerMembers}
                onCancelClick={onClose}
            />
        </ModalBase>
    );
};

CartUpsellMembershipModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    limitToMemberId: PropTypes.number,
    onClose: PropTypes.func.isRequired,
};

export default CartUpsellMembershipModal;
